<template>
  <v-container
    fluid
  >
    <v-row
      class="docs align-start justify-center"
      wrap
    >
      <v-col>
        <v-row class="justify-space-between px-2">
          <v-col class="col-auto">
            <span class="headline font-weight-bold">Documentation</span>
          </v-col>

          <v-col class="col-auto">
            <v-row>
              <v-col>
                <!-- View as External Toggle -->
                <v-chip
                  v-if="isInternalUser"
                  small
                  :color="viewAsExternal ? 'warning' : 'default'"
                  :outlined="!viewAsExternal"
                  class="cursor-pointer"
                  @click="viewAsExternal = !viewAsExternal"
                >
                  <font-awesome-icon
                    :icon="['fas', viewAsExternal ? 'eye' : 'eye-slash']"
                    class="mr-1"
                    size="sm"
                  />
                  {{ viewAsExternal ? 'Viewing as External' : 'View as External' }}
                </v-chip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col>
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <!-- Navigation -->
          <v-col
            class="col-auto docs-nav-col"
            style="min-width: 260px; max-width: 280px;"
          >
            <!-- Navigation List -->
            <v-list
              dense
              nav
              class="docs-nav-list"
            >
              <!-- Static "Getting Started" item -->
              <v-list-item
                :class="[
                  'docs-nav-item docs-nav-item--top',
                  { 'docs-nav-item--active': isNavItemActive('getting-started') },
                ]"
                @click="selectNavItem({ title: 'Getting Started', reportId: 'getting-started' })"
              >
                <v-list-item-icon class="docs-nav-item-icon mr-2">
                  <font-awesome-icon
                    :icon="['fas', 'rocket']"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    <router-link
                      :to="docPath('getting-started')"
                      class="docs-nav-link"
                      :class="{ 'docs-nav-link--active': isNavItemActive('getting-started') }"
                    >
                      Getting Started
                    </router-link>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider class="my-2 mx-2" />

              <!-- Navigation Groups -->
              <v-list-group
                v-for="group in navGroups"
                :key="group.id"
                :value="isGroupExpanded(group)"
                no-action
                sub-group
                class="docs-nav-section"
              >
                <template v-slot:activator>
                  <v-list-item-content>
                    <v-list-item-title class="docs-nav-group-title">
                      {{ group.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>

                <v-list-item
                  v-for="item in group.items"
                  :key="item.reportId"
                  :class="[
                    'docs-nav-item',
                    { 'docs-nav-item--active': isNavItemActive(item.reportId) },
                  ]"
                  @click="selectNavItem(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <router-link
                        :to="docPath(item.reportId)"
                        class="docs-nav-link"
                        :class="{ 'docs-nav-link--active': isNavItemActive(item.reportId) }"
                      >
                        {{ item.title }}
                      </router-link>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </v-list>
          </v-col>

          <!-- Content -->
          <v-col>
            <v-card class="elevation-0">
              <v-card-text
                v-if="reportId === 'getting-started'"
                class="pa-6"
              >
                <v-row>
                  <GettingStartedContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'guides'"
                class="pa-6"
              >
                <v-row>
                  <RouteGuidesContent :show-internal-content="showInternalContent" />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'delivery'"
                class="pa-6"
              >
                <v-row>
                  <DeliveryContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'search'"
                class="pa-6"
              >
                <v-row>
                  <SearchContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'concepts'"
                class="pa-6"
              >
                <v-row>
                  <ConceptsContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'troubleshooting'"
                class="pa-6"
              >
                <v-row>
                  <TroubleshootingContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'dashboard'"
                class="pa-6"
              >
                <v-row>
                  <DocsLanding :show-internal-content="showInternalContent" />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'release-notes'"
                class="pa-6"
              >
                <v-row>
                  <ReleaseNotes />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'release-summary'"
                class="pa-6"
              >
                <v-row>
                  <ReleaseSummary />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'release-kit'"
                class="pa-6"
              >
                <v-row>
                  <ReleaseKit />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'features'"
                class="pa-6"
              >
                <v-row>
                  <FeaturesContent :show-internal-content="showInternalContent" />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'feedback'"
                class="pa-6"
              >
                <v-row>
                  <FeedbackContent />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'filters'"
                class="pa-6"
              >
                <v-row>
                  <FiltersContent :show-internal-content="showInternalContent" />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'other' || reportId === 'legal' || reportId === 'privacy-policy'"
                class="pa-6"
              >
                <v-row>
                  <PrivacyPolicy />
                </v-row>
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'test' || isTestSubroute"
                class="pa-0"
              >
                <TestChecklists />
              </v-card-text>

              <v-card-text
                v-else-if="reportId === 'help-coverage'"
                class="pa-6"
              >
                <v-row>
                  <HelpCoverageContent />
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'DocApp',
  components: {
    ConceptsContent: () => import('@/docs/components/ConceptsContent.vue'),
    DeliveryContent: () => import('@/docs/components/DeliveryContent.vue'),
    DocsLanding: () => import('@/docs/components/DocsLanding.vue'),
    FeedbackContent: () => import('@/docs/components/FeedbackContent.vue'),
    FeaturesContent: () => import('@/docs/components/FeaturesContent.vue'),
    FiltersContent: () => import('@/docs/components/FiltersContent.vue'),
    GettingStartedContent: () => import('@/docs/components/GettingStartedContent.vue'),
    HelpCoverageContent: () => import('@/docs/components/HelpCoverageContent.vue'),
    PrivacyPolicy: () => import('@/docs/views/PrivacyPolicy.vue'),
    ReleaseKit: () => import('@/docs/views/ReleaseKit.vue'),
    ReleaseNotes: () => import('@/docs/views/ReleaseNotes.vue'),
    ReleaseSummary: () => import('@/docs/views/ReleaseSummary.vue'),
    RouteGuidesContent: () => import('@/docs/components/RouteGuidesContent.vue'),
    SearchContent: () => import('@/docs/components/SearchContent.vue'),
    TestChecklists: () => import('@/docs/views/TestChecklists.vue'),
    TroubleshootingContent: () => import('@/docs/components/TroubleshootingContent.vue'),
  },
  data() {
    return {
      viewAsExternal: false,
      baseNavGroups: [
        {
          id: 'product',
          title: 'Product',
          items: [
            { title: 'Route Guides', subtitle: 'Dashboard, projects, and search walkthroughs', reportId: 'guides' },
            { title: 'Delivery Reports', subtitle: 'Pacing and discrepancy workflows', reportId: 'delivery' },
            { title: 'Search and Context', subtitle: 'Global search behavior and keyboard flow', reportId: 'search' },
            { title: 'Concepts', subtitle: 'Glossary and core product concepts', reportId: 'concepts' },
            { title: 'Troubleshooting', subtitle: 'Resolve common onboarding issues', reportId: 'troubleshooting' },
            { title: 'Docs', subtitle: 'Learn the basics of iAdOps', reportId: 'dashboard' },
          ],
        },
        {
          id: 'releases',
          title: 'Releases',
          items: [
            { title: 'Release Summary', subtitle: 'Share executive highlights quickly', reportId: 'release-summary' },
            { title: 'Release Notes', subtitle: 'Review the latest updates', reportId: 'release-notes' },
            { title: 'Release Kit', subtitle: 'Copy stakeholder communication templates', reportId: 'release-kit', internalOnly: true },
          ],
        },
        {
          id: 'reference',
          title: 'Reference',
          items: [
            { title: 'Features', subtitle: 'Overview of platform capabilities', reportId: 'features' },
            { title: 'Feedback', subtitle: 'Submit product feedback and understand processing', reportId: 'feedback' },
            { title: 'Filters', subtitle: 'Learn more about filtering.', reportId: 'filters' },
            { title: 'Privacy', subtitle: 'Policies, guidelines, and legal information', reportId: 'privacy-policy' },
          ],
        },
        {
          id: 'qa',
          title: 'QA',
          items: [
            { title: 'QA Checklists', subtitle: 'Test checklists for QA', reportId: 'test', devOnly: true },
            { title: 'Help Coverage Audit', subtitle: 'Tour and guide coverage per route', reportId: 'help-coverage', devOnly: true },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapAuthGetters(['isUserInternalDev', 'isInternalUser']),

    showInternalContent() {
      return this.isInternalUser && !this.viewAsExternal
    },

    navGroups() {
      return this.baseNavGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (item.internalOnly && !this.showInternalContent) return false
            if (item.devOnly && !this.isUserInternalDev) return false
            return true
          }),
        }))
        .filter((group) => group.items.length > 0)
    },

    reportId() {
      const { reportName, reportId } = this.$route.params
      const currentReportId = reportId || reportName || 'getting-started'

      if (currentReportId === 'releases') {
        return 'release-notes'
      }

      // Check both reportName (first param) and reportId (second param)
      // Router path is /docs/:reportName?/:reportId?
      return currentReportId
    },

    isTestSubroute() {
      // Handle /docs/test/:checklistId routes
      const { reportName } = this.$route.params
      return reportName === 'test'
    },
  },
  watch: {
    '$route.params': {
      handler() {
        // Scroll to top when route changes.
        window.scrollTo(0, 0)
      },
      deep: true,
      immediate: true,
    },
  },
  async created() {
    // Self-managed view: load own data
    await this.$store.dispatch('tools/setView', 'docs')
  },
  methods: {
    isGroupExpanded(group) {
      return group.items.some((item) => this.isNavItemActive(item.reportId))
    },

    docPath(reportId) {
      if (reportId === 'releases') {
        return '/docs/release-notes'
      }

      return `/docs/${reportId}`
    },

    isNavItemActive(reportId) {
      if (reportId === 'release-notes') {
        return ['release-notes', 'releases'].indexOf(this.reportId) > -1
      }

      return this.reportId === reportId
    },

    selectNavItem(item) {
      // Navigate to the selected documentation item.
      this.$router.push({ path: this.docPath(item.reportId) })

      // Log event to Google Analytics.
      this.$analytics.event('goal_completion', {
        category: 'View Documentation',
        description: 'Navigate to Doc',
        label: `Navigated to doc titled "${item.title}"`,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

// ── Sidebar column ──
.docs-nav-col {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  padding-right: 0;
}

// ── Navigation list ──
.docs-nav-list {
  padding-top: 8px;
}

// ── Group section spacing ──
.docs-nav-section {
  margin-bottom: 4px;
}

// ── Group titles ──
.docs-nav-group-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: $color-monochrome-gray;
  min-height: 22px;
  line-height: 22px;
}

// ── Nav items (shared) ──
.docs-nav-item {
  min-height: 36px;
  border-radius: 6px;
  margin: 1px 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.docs-nav-item--active {
  background-color: rgba($color-monochrome-brightblue, 0.08);

  &:hover {
    background-color: rgba($color-monochrome-brightblue, 0.12);
  }
}

// ── Top-level "Getting Started" alignment ──
.docs-nav-item--top {
  margin-top: 2px;
  margin-left: 20px; // align with sub-group item indent
  padding-left: 8px;
}

.docs-nav-item-icon {
  align-self: center;
  color: $color-monochrome-gray;
  min-width: 20px;
}

.docs-nav-item--active .docs-nav-item-icon {
  color: $color-monochrome-brightblue;
}

// ── Links ──
.docs-nav-link {
  font-size: 13px;
  font-weight: 400;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;
}

.docs-nav-link--active {
  font-weight: 600;
  color: $color-monochrome-brightblue;
}

// ── Content cards ──
.docs {
  .docs-card {
    border-radius: 6px;
    box-shadow: 0 2px 6px 0 rgba(0, 148, 255, 0.21);
  }

  .card-title {
    font-size: medium;
    font-weight: bold;
    letter-spacing: 0.69px;
    line-height: 29px;
  }

  .welcome-card {
    .card-title {
      color: $color-monochrome-brightblue;
      justify-content: center;
    }
  }
}

// ── Dark mode overrides ──
.theme--dark {
  .docs-nav-col {
    border-right-color: rgba(255, 255, 255, 0.08);
  }

  .docs-nav-item:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }

  .docs-nav-item--active {
    background-color: rgba($color-monochrome-brightblue, 0.14);

    &:hover {
      background-color: rgba($color-monochrome-brightblue, 0.18);
    }
  }

  .docs-nav-group-title {
    color: $color-monochrome-midgray;
  }

  .docs-nav-item-icon {
    color: $color-monochrome-midgray;
  }
}
</style>
